import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Chip
} from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import { ProjectProcessingDetail, getProjectActionLabel } from "../api/models/ReportModels";

interface ReportDetailsGridProps {
    details: ProjectProcessingDetail[];
    initialActionFilter?: string;
}

const ReportDetailsGrid = ({ details, initialActionFilter = "" }: ReportDetailsGridProps): JSX.Element => {
    const [projectNameFilter, setProjectNameFilter] = useState("");
    const [costCenterFilter, setCostCenterFilter] = useState("");
    const [actionFilter, setActionFilter] = useState<string>(initialActionFilter);

    useEffect(() => {
        setActionFilter(initialActionFilter);
    }, [initialActionFilter]);

    const filteredDetails = useMemo(() => {
        return details.filter((detail) => {
            const matchesProjectName = !projectNameFilter || 
                detail.projectName.toLowerCase().includes(projectNameFilter.toLowerCase());
            const matchesCostCenter = !costCenterFilter || 
                detail.costCenter.toLowerCase().includes(costCenterFilter.toLowerCase());
            const matchesAction = actionFilter === "" || detail.action === actionFilter;

            return matchesProjectName && matchesCostCenter && matchesAction;
        });
    }, [details, projectNameFilter, costCenterFilter, actionFilter]);

    const getActionChipColor = (action: string): "success" | "error" | "warning" | "default" | "info" => {
        switch (action) {
            case "Unflagged":
                return "success";
            case "Locked":
                return "error";
            case "FlagAdvanced":
                return "warning";
            case "NewlyFlagged":
                return "warning";
            case "Valid":
                return "success";
            case "Error":
                return "error";
            default:
                return "default";
        }
    };

    return (
        <Box sx={{ width: "92%", pb: 0, mb: "70px" }}>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" gap={2}>
                    <TextField
                        label="Project Name"
                        variant="outlined"
                        size="small"
                        value={projectNameFilter}
                        onChange={(e) => setProjectNameFilter(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Cost Center"
                        variant="outlined"
                        size="small"
                        value={costCenterFilter}
                        onChange={(e) => setCostCenterFilter(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "40%" }}>
                                <b>Project Name</b>
                            </TableCell>
                            <TableCell sx={{ width: "30%" }}>
                                <b>Cost Center</b>
                            </TableCell>
                            <TableCell sx={{ width: "30%" }}>
                                <b>Action</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredDetails.map((detail) => (
                            <TableRow key={`${detail.projectId}-${detail.action}`} hover>
                                <TableCell>{detail.projectName}</TableCell>
                                <TableCell>{detail.costCenter}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={getProjectActionLabel(detail.action)}
                                        color={getActionChipColor(detail.action)}
                                        size="small"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ReportDetailsGrid;
